import { Affix, Button, Collapse, message } from 'antd';
import React, { useState } from 'react';
import './SupplierCard.scss';
import Markdown from 'react-markdown';
import { SupplierBriefingData } from 'models/SupplierDynamics';
import { SupplierTitle } from '../../models/Supplier';
import { SupplierTabType } from '../../sb/shared/constants';
import { FeedbackContent } from './FeedbackContent';
import remarkGfm from 'remark-gfm';
import { EditOutlined, ExportOutlined } from '@ant-design/icons';
import { useRenderedHtml } from 'hooks';
import { useUtilsApi } from 'api/useUtilsApi';
import fileDownload from 'js-file-download';
import { ACLCheck } from 'shared/Helpers';

const { Panel } = Collapse;

interface SupplierIQContentProps {
  briefingReport: SupplierBriefingData;
  handleToggleOpen?: () => void;
  activeKey?: number[];
  supplier?: SupplierTitle | undefined;
  onEditClick?: (content: string, title?: string) => void;
}

export const SupplierIQContent: React.FC<SupplierIQContentProps> = ({
  briefingReport,
  handleToggleOpen,
  activeKey,
  supplier,
  onEditClick
}): React.ReactElement => {
  const { ref, html } = useRenderedHtml<HTMLDivElement>();
  const { htmlToPdf } = useUtilsApi();
  const [isExporting, setIsExporting] = useState(false);
  const reportType = 'SupplierIQ Report';
  const hasBriefingContent = Boolean(briefingReport?.briefing);

  const handlePdfClick = async (): Promise<void> => {
    if (!html) {
      message.warning('No content available to export');
      return;
    }

    if (!supplier?.name) {
      message.warning('Supplier name is required for PDF export');
      return;
    }

    setIsExporting(true);
    try {
      const pdfBlob = await htmlToPdf(html, supplier.name, reportType);
      const filename = `NPI Supplier IQ-${supplier.name}-${reportType}.pdf`;
      fileDownload(pdfBlob, filename);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex">
      <div className="disclaimer-collapse w-11/12">
        <Collapse
          // ghost
          expandIconPosition="end"
          expandIcon={() =>
            activeKey?.length === 0 ? (
              <div style={{ fontSize: 13 }}>Show disclaimer</div>
            ) : (
              <div style={{ fontSize: 13 }}>Hide disclaimer</div>
            )
          }
          onChange={handleToggleOpen}
          activeKey={activeKey}
          className="mb-5"
        >
          <Panel header={<div className="text-lg text-npi-blue">Supplier IQ™ Disclaimer</div>} key={1} showArrow={true}>
            <div className="-mt-1">
              This report was generated using artificial intelligence and 100% public information. AI can make mistakes;
              consider checking important information.
            </div>
          </Panel>
        </Collapse>
        {ACLCheck(['update:supplier_iq_report']) && hasBriefingContent && (
          <div className="flex justify-end mb-4">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              className="!bg-[#0070FF] !border-[#0070FF] !px-4 !py-2 !h-auto"
              onClick={() => onEditClick?.(briefingReport?.briefing || '', 'Edit Supplier IQ')}
            >
              Edit Information
            </Button>
          </div>
        )}
        <div className="markdown" ref={ref}>
          <Markdown remarkPlugins={[remarkGfm]}>{briefingReport?.briefing}</Markdown>
        </div>
      </div>
      <Affix offsetTop={250}>
        <div className="flex flex-col gap-4 items-center text-center">
          <Button
            type="link"
            icon={<ExportOutlined />}
            onClick={handlePdfClick}
            loading={isExporting}
            className="!text-[#0F62FE] !h-auto flex items-center gap-1 font-medium"
          >
            Export to PDF
          </Button>
          <div className="w-full flex justify-center">
            <FeedbackContent id={briefingReport?.id} supplierTabType={SupplierTabType.BRIEFING} />
          </div>
        </div>
      </Affix>
    </div>
  );
};
